import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { CurrentUser } from '@/models/CurrentUser';
import { UserRole } from '@/permissions/types';
import { permissionService } from '@/permissions/PermissionService';

interface CurrentUserState {
  // User data
  user: CurrentUser | null;
  isLoading: boolean;
  error: string | null;
  hasFreshUserData: boolean;

  // Actions
  setUser: (user: CurrentUser | null) => void;
  clearUser: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Permission methods (delegates to PermissionService)
  hasPermission: (roles: UserRole[]) => boolean;
  hasAllPermissions: (roles: UserRole[]) => boolean;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: () => boolean;
  canAccessFeature: (feature: string) => boolean;
}

/**
 * Current User Store
 * Manages authenticated user state and permissions
 */
export const useCurrentUserStore = create<CurrentUserState>()(
  devtools(
    (set, get) => ({
      // Initial state
      user: null,
      isLoading: false,
      error: null,
      hasFreshUserData: false,

      // Set user and update permission service
      setUser: (user) => {
        set({ user, error: null, hasFreshUserData: !!user });
        if (user) {
          permissionService.setUserRoles(user.roleCodes);
        } else {
          permissionService.clearRoles();
        }
      },

      // Clear user on logout
      clearUser: () => {
        set({ user: null, error: null, hasFreshUserData: false });
        permissionService.clearRoles();
      },

      // Set loading state
      setLoading: (loading) => set({ isLoading: loading }),

      // Set error state
      setError: (error) => set({ error }),

      // Permission check methods - delegate to PermissionService
      hasPermission: (roles) => {
        const { user } = get();
        if (!user) {
          return false;
        }
        const result = permissionService.hasPermission(roles);
        return result;
      },

      hasAllPermissions: (roles) => {
        const { user } = get();
        if (!user) return false;
        return permissionService.hasAllPermissions(roles);
      },

      hasRole: (role) => {
        const { user } = get();
        if (!user) return false;
        return permissionService.hasRole(role);
      },

      hasAnyRole: () => {
        const { user } = get();
        if (!user) return false;
        return permissionService.hasAnyRole();
      },

      canAccessFeature: (feature) => {
        const { user } = get();
        if (!user) return false;
        return permissionService.canAccessFeature(feature);
      },
    }),
    { name: 'CurrentUserStore' }
  )
);

/**
 * Selector hooks for common use cases
 */
export const useUser = () => useCurrentUserStore((state) => state.user);
export const useUserLoading = () => useCurrentUserStore((state) => state.isLoading);
export const useUserError = () => useCurrentUserStore((state) => state.error);
export const useIsAuthenticated = () => useCurrentUserStore((state) => !!state.user);

// Stable empty array to prevent unnecessary re-renders
const EMPTY_ROLES: UserRole[] = [];
export const useUserRoles = () =>
  useCurrentUserStore((state) => state.user?.roleCodes ?? EMPTY_ROLES);
